import { ref, computed, toRaw, watch, useTemplateRef } from 'vue'

import type { Ref, ComputedRef, CSSProperties } from 'vue'
import type { MultiselectSearchOption, MultiSelectSearchableProps } from '../types/interfaces'

import { useDropdownPosition } from '../../composables/dropdown-position.hook'

interface MultiSelectSearchableOptions {
  props: MultiSelectSearchableProps
  onSelectBefore: (option: MultiselectSearchOption) => void
  onDeselectBefore: (option: MultiselectSearchOption) => void
  onSelected: (options: MultiselectSearchOption[]) => void
  onValidation: (valid: boolean) => void
  onDropdownClose: () => void
}

interface MultiSelectSearchableReturn {
  refUpdateCount: Ref<number>
  refAllSelected: Ref<boolean>
  refOpen: Ref<boolean>
  refValue: Ref<(string | number)[]>
  refOptions: Ref<MultiselectSearchOption[]>
  refFocused: Ref<boolean>
  selectSearch: Readonly<Ref<HTMLElement | null>>
  isDisabled: ComputedRef<boolean>
  placeholderClass: ComputedRef<string>
  inputText: ComputedRef<string>
  dropdownStyle: ComputedRef<CSSProperties | null>
  handleDropdownShouldOpen: (value: boolean) => void
  handleBeforeSelect: (option: MultiselectSearchOption) => void
  handleBeforeDeselect: (option: MultiselectSearchOption) => void
  handleSelected: (options: MultiselectSearchOption[]) => void
  handleDeselected: (option: MultiselectSearchOption) => void
  handleFocus: () => void
  handleBlur: () => void
  handleSearch: () => void
  handleClickOutside: () => void
  handleClickClose: () => void
  handleOpen: () => void
  setToggleAll: () => void
}

export const useMultiSelectSearchable = ({
  props,
  onSelectBefore,
  onDeselectBefore,
  onSelected,
  onValidation,
  onDropdownClose,
}: MultiSelectSearchableOptions): MultiSelectSearchableReturn => {
  const refUpdateCount = ref(1)
  const refAllSelected = ref(props.options.length === (props.value?.length ?? 0))
  const refOpen = ref(false)
  const refValue = ref<(string | number)[]>(props.value || [])
  const refOptions = ref<MultiselectSearchOption[]>([...props.options])
  const refFocused = ref(false)

  const selectSearch = useTemplateRef<HTMLElement>('selectSearch')

  const { dropdownStyle, setUpdateDropdownHeight } = useDropdownPosition({
    elementRef: selectSearch,
    isAvailablePosition: props.isAvailablePosition ?? false,
  })

  const isDisabled = computed(() => {
    return !Array.isArray(refOptions.value) || refOptions.value.length === 0 || props.disabled || false
  })

  const handleDropdownShouldOpen = (value: boolean): void => {
    const isClosing = refOpen.value && !value

    refOpen.value = value

    if (isClosing) {
      onDropdownClose()
    }
  }

  const handleBeforeSelect = (option: MultiselectSearchOption): void => {
    onSelectBefore(toRaw(option))
  }

  const handleBeforeDeselect = (option: MultiselectSearchOption): void => {
    onDeselectBefore(toRaw(option))
  }

  const setOptionsSelect = (options: MultiselectSearchOption[]): void => {
    const values = options.map((item) => item.value)
    refOptions.value = toRaw(refOptions.value).map((item) => ({
      ...item,
      selected: values.includes(item.value),
    }))
  }

  const handleSelected = (options: MultiselectSearchOption[]): void => {
    setOptionsSelect(options)
    refAllSelected.value = options.length === toRaw(refOptions.value).length
    onSelected(toRaw(options))
  }

  const setOptionDeselect = (option: MultiselectSearchOption): void => {
    const value = option.value
    refOptions.value = toRaw(refOptions.value).map((item) => ({
      ...item,
      selected: item.value === value ? false : item.selected,
    }))
  }

  const handleDeselected = (option: MultiselectSearchOption): void => {
    setOptionDeselect(option)
    refAllSelected.value = false
    const options = toRaw(refOptions.value).filter(opt => opt.selected)
    onSelected(options)
  }

  const handleFocus = (): void => {
    refFocused.value = true
    handleDropdownShouldOpen(true)
  }

  const handleBlur = (): void => {
    refFocused.value = false
  }

  const handleSearch = (): void => {
    handleDropdownShouldOpen(true)
  }

  const handleClickOutside = (): void => {
    refFocused.value = false
    handleDropdownShouldOpen(false)
  }

  const handleClickClose = (): void => {
    if (refOpen.value) {
      handleDropdownShouldOpen(false)
    }
  }

  const placeholderClass = computed(() => {
    const selectedOptions = toRaw(refOptions.value)
      .filter(option => option.selected)
      .map(option => option.text)
    return selectedOptions.length > 0 && !refFocused.value
      ? 'view-text'
      : 'view-placeholder'
  })

  const inputText = computed(() => {
    const selectedOptions = toRaw(refOptions.value)
      .filter(option => option.selected)
      .map(option => option.text)

    const isAllOptions = selectedOptions.length === refOptions.value.length

    if (isAllOptions && props.isAllAvailable) return 'All'
    return selectedOptions.length > 0
      ? selectedOptions.join(', ')
      : (props.placeholder ?? '')
  })

  const setToggleAll = (): void => {
    const value = !refAllSelected.value
    refAllSelected.value = value

    refOptions.value = toRaw(refOptions.value).map(option => ({
      ...option,
      selected: value,
    }))
    refValue.value = value ? refOptions.value.map(option => option.text) : []

    refUpdateCount.value++

    const options = toRaw(refOptions.value).filter(option => option.selected)
    onSelected(toRaw(options))
  }

  const handleOpen = (): void => {
    setUpdateDropdownHeight()
  }

  watch(
    () => refOptions.value,
    () => {
      const selectedOptions = refOptions.value.filter(option => option.selected)
      const valid = props.required
        ? selectedOptions.length > 0
        : true
      onValidation(valid)
    },
  )

  return {
    refUpdateCount,
    refAllSelected,
    refOpen,
    refValue,
    refOptions,
    refFocused,
    selectSearch,
    isDisabled,
    placeholderClass,
    inputText,
    dropdownStyle,
    handleDropdownShouldOpen,
    handleBeforeSelect,
    handleBeforeDeselect,
    handleSelected,
    handleDeselected,
    handleFocus,
    handleBlur,
    handleSearch,
    handleClickOutside,
    handleClickClose,
    handleOpen,
    setToggleAll,
  }
}
